import { TransactionStatus } from "../contract/models";
import { IApiResponse } from "../../models";

export interface ICompletedTransferResponse extends IApiResponse {
  data: ICompletedTransferResponseData;
}

export interface ICompletedTransferResponseData {
  content: ICompletedTransferBody[];
  empty: boolean;
  first: boolean;
  last: boolean;
  number: number;
  numberOfElements: number;
  totalElements: number;
  totalPages: number;
}

/**
 * Who triggered the transfer. `requesterId` is a user id for a human requester
 * and the `AUTO_CASH_OUT_SERVICE` sentinel when the automated cashout service
 * did it, so it is not always a navigable user.
 */
export const AUTO_CASH_OUT_SERVICE_REQUESTER_ID = "AUTO_CASH_OUT_SERVICE";

export enum ETransferRequesterType {
  USER = "USER",
  ADMIN = "ADMIN",
  SERVICE = "SERVICE",
}

/**
 * Casing and separators are stripped before comparing: the exact spelling of
 * the sentinel is not pinned down yet, so AUTO_CASH_OUT_SERVICE,
 * AUTO_CASHOUT_SERVICE and auto-cashout-service all have to match.
 */
const normalizeRequesterSentinel = (value?: string): string =>
  (value || "").replace(/[^a-z0-9]/gi, "").toUpperCase();

/**
 * Whether the transfer was made by the automated cashout service rather than a
 * person. Such a requester is not a user, so it never links to a user page.
 */
export const isAutoCashOutServiceRequester = (
  transfer: Pick<
    ICompletedTransferBody,
    "requesterId" | "requesterName" | "requesterType"
  >
): boolean => {
  const sentinel = normalizeRequesterSentinel(
    AUTO_CASH_OUT_SERVICE_REQUESTER_ID
  );

  return (
    normalizeRequesterSentinel(transfer.requesterId) === sentinel ||
    normalizeRequesterSentinel(transfer.requesterName) === sentinel ||
    transfer.requesterType === ETransferRequesterType.SERVICE
  );
};

export interface ICompletedTransferBody {
  id: number;
  transactionId: string;
  referenceTransactionId: string;
  status: TransactionStatus;
  domainId: string;
  gatewayPaymentId: string;
  amount: number;
  beneficiaryId: string;
  beneficiaryName: string;
  paymentTransactionType: "MANUAL" | "AUTO" | "REFUND" | "MULTI";
  doneThru: "BANK" | "TABBY" | "MOYASAR" | "HYPERPAY";
  createdAt: string;
  updatedAt: string;
  manualWithdrawId: number;
  /**
   * Requester fields — not served yet, pending the transferred-api change.
   * Typed as optional so the UI degrades to a placeholder until they land.
   */
  requesterId?: string;
  requesterName?: string;
  /** Kept open to raw strings: the backend value set is not final yet. */
  requesterType?: ETransferRequesterType | string;
}
